import { cn } from '@/utils/cn';
// Одна заявка: чек крупно, поля, решение. Сценарии 49 и 55.
//
// Панель, а не деталь: заявку открывают из двух мест — из очереди приёмки и из карточки лица,
// — и ни в одном из них она не должна вытеснять контекст, в котором человек работает.
//
// У принятой заявки НЕТ кнопки «Изменить». Её нет вообще: правки денежной записи не
// существует, есть сторно — обратная запись с основанием (правило 8). Сторно делает только
// руководство, и без права кнопка не сереет, а объясняет, к кому идти.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks } from '@/shared/money/format';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import { КЕГЛЬ_ВЕЛИЧИНЫ } from '@/shared/ui/metric';
import * as Alert from '@/shared/ui/alert';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import { AttachmentImage } from '@/shared/ui/attachment';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextField } from '@/shared/ui/text-field';

import { CLAIM_STATUS_TITLES, CLAIM_STATUS_TONE } from '../dictionaries';
import { useClaim, useDecideClaim } from '../queries';

const ACCEPT_PERMISSION = 'core.accountable.claim_accept';
const REJECT_PERMISSION = 'core.accountable.claim_reject';
const REVERSE_PERMISSION = 'core.accountable.reverse';

export function ClaimPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const права = useAuthStore((state) => state.permissions);
  const заявка = useClaim(id);
  const решение = useDecideClaim();

  const [причина, setПричина] = useState('');
  const [запрет, setЗапрет] = useState<string | null>(null);
  const [ошибкаПричины, setОшибкаПричины] = useState<string | undefined>(undefined);

  function решить(вид: 'accept' | 'reject' | 'return' | 'reverse') {
    const нужное =
      вид === 'accept'
        ? ACCEPT_PERMISSION
        : вид === 'reverse'
          ? REVERSE_PERMISSION
          : REJECT_PERMISSION;
    if (!can(права, нужное)) {
      setЗапрет(
        вид === 'reverse'
          ? 'Сторно принятой заявки делают руководство и администратор. Скопируйте ссылку на эту заявку из адресной строки и передайте им.'
          : 'Принимать и отклонять заявки может финансовый отдел.',
      );
      return;
    }
    if (решение.isPending) {
      setЗапрет('Решение уже отправлено, ждём ответ сервера.');
      return;
    }
    if (вид !== 'accept' && причина.trim() === '') {
      setОшибкаПричины(
        вид === 'reverse'
          ? 'Основание сторно обязательно: без него в журнале останется отмена без единого слова о том, почему.'
          : 'Причина обязательна: человеку в поле надо понять, что исправить.',
      );
      return;
    }
    setОшибкаПричины(undefined);
    setЗапрет(null);
    решение.mutate({ claimId: id, decision: вид, reason: причина.trim() });
  }

  function отправить(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
  }

  return (
    <ScreenState
      data={заявка.data}
      error={заявка.error as never}
      subject='заявку подотчёта'
      isEmpty={() => false}
      loading={
        <div className='flex flex-col gap-3'>
          <Skeleton className='h-40 w-full rounded-lg' />
          <Skeleton className='h-6 w-48' />
        </div>
      }
      empty={null}
      onRetry={() => void заявка.refetch()}
    >
      {(строка) => (
        <form className='flex flex-col gap-4' onSubmit={отправить}>
          {(строка.receipts?.length ?? 0) > 0 ? (
            <AttachmentImage
              url={строка.receipts?.[0]?.url ?? null}
              alt={`Чек по заявке «${строка.title}»`}
              что='чек'
              нетАдреса='Адрес снимка не пришёл вместе с заявкой. Запись о чеке есть, открыть его нечем — сообщите администратору.'
            />
          ) : (
            // «Без чека» — осознанная отметка человека, а не отсутствие данных.
            <Alert.Root variant='lighter' status='information' size='small'>
              <Alert.Icon />
              Расход без подтверждающего документа: человек отметил это явно.
            </Alert.Root>
          )}

          <div className='flex items-baseline justify-between gap-3'>
            <span className={cn(КЕГЛЬ_ВЕЛИЧИНЫ.важный, 'tabular-nums text-text-strong-950')}>
              {formatKopecks(строка.amount)}
            </span>
            <Badge.Root variant='lighter' color={CLAIM_STATUS_TONE[строка.status]} size='medium'>
              {CLAIM_STATUS_TITLES[строка.status]}
            </Badge.Root>
          </div>

          <FieldList>
            <FieldRow label='Наименование' value={строка.title} />
            <FieldRow label='Категория' value={строка.category_title} />
            <FieldRow label='Подотчётное лицо' value={строка.person_name} />
            <FieldRow
              label='Кому выдано'
              value={строка.recipient_name}
              missing='получатель не указан: встречной проводки не будет'
            />
            <FieldRow
              label='Дата траты'
              value={строка.spent_at === null ? null : formatDateOnly(строка.spent_at)}
              missing='не указана, взята дата подачи'
            />
            {/* Строка показывается ТОЛЬКО когда решение уже вынесено. У поданной заявки
                решения ещё нет, и «решение без причины не выносилось» читалось загадкой:
                человек перечитывал фразу и пытался понять, что она ему сообщает. */}
            {строка.status !== 'submitted' && (
              <FieldRow
                label='Причина решения'
                value={строка.reject_reason}
                missing='решение вынесено без письменной причины'
              />
            )}
          </FieldList>

          {строка.debt_id !== null && (
            <Alert.Root variant='lighter' status='information' size='small'>
              <Alert.Icon />
              {строка.debt_annulled
                ? 'Долг получателя аннулирован вместе со сторно заявки.'
                : 'При приёмке получателю заведён долг на эту сумму.'}
              {строка.debt_repaid !== null && строка.debt_repaid > 0 && (
                <>
                  {' '}
                  По нему уже удержано {formatKopecks(строка.debt_repaid)} — возврат этих денег
                  оформляется отдельно.
                </>
              )}
            </Alert.Root>
          )}

          {строка.status === 'submitted' && (
            <TextField
              id='claim-reason'
              label='Причина отклонения или возврата'
              value={причина}
              onChange={setПричина}
              placeholder='чек нечитаемый, пришлите заново'
              error={ошибкаПричины}
              hint='Для приёмки не нужна. Для отклонения и возврата обязательна.'
            />
          )}
          {строка.status === 'accepted' && (
            <>
              <TextField
                id='claim-basis'
                label='Основание сторно'
                value={причина}
                onChange={setПричина}
                placeholder='расход отнесён не на то юрлицо'
                error={ошибкаПричины}
              />
              {/*
                Правило 8: сторно ПОКАЗЫВАЕТ ЛИСТ ПОСЛЕДСТВИЙ, а не спрашивает «вы уверены».
                Здесь его не было вовсе: человек нажимал красную кнопку и узнавал, что случилось,
                уже после. Проверено на стенде: заявка не исчезает, а получает состояние
                «сторнирована», рядом появляется обратная запись на ту же сумму, и остаток
                подотчётного лица меняется на неё же.

                Про долг получателя здесь намеренно ничего не написано: сервер в ответе на
                отдельную заявку поля долга не заполняет вовсе, и любая фраза о нём была бы
                догадкой. Это отмечено серверному потоку.
              */}
              <Alert.Root variant='lighter' status='warning' size='small'>
                <Alert.Icon />
                Что произойдёт: заявка останется в списке перечёркнутой, рядом появится обратная
                запись на {formatKopecks(строка.amount)}, а остаток {строка.person_name} увеличится
                на эту же сумму. Отменить сторно нельзя — оно само и есть отмена.
              </Alert.Root>
            </>
          )}

          {запрет !== null && (
            <Alert.Root variant='lighter' status='warning' size='small'>
              <Alert.Icon />
              {запрет}
            </Alert.Root>
          )}
          {решение.error !== null && <ErrorAlert error={решение.error as never} />}
          {решение.data !== undefined && (
            <Alert.Root variant='lighter' status='success' size='small'>
              <Alert.Icon />
              Состояние заявки: {CLAIM_STATUS_TITLES[решение.data.status]}.
              {решение.data.balance_after !== null && (
                <> Остаток человека стал {formatKopecks(решение.data.balance_after)}.</>
              )}
            </Alert.Root>
          )}

          <div className='flex flex-wrap gap-2'>
            {строка.status === 'submitted' && (
              <>
                <Button.Root
                  type='button'
                  variant='primary'
                  mode='filled'
                  size='small'
                  onClick={() => решить('accept')}
                >
                  {решение.isPending ? 'Принимаем…' : 'Принять'}
                </Button.Root>
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='stroke'
                  size='small'
                  onClick={() => решить('return')}
                >
                  Вернуть на доработку
                </Button.Root>
                <Button.Root
                  type='button'
                  variant='error'
                  mode='stroke'
                  size='small'
                  onClick={() => решить('reject')}
                >
                  Отклонить
                </Button.Root>
              </>
            )}
            {строка.status === 'accepted' && (
              // Кнопки «Изменить» здесь нет и не будет: правки денежной записи не существует.
              <Button.Root
                type='button'
                variant='error'
                mode='stroke'
                size='small'
                onClick={() => решить('reverse')}
              >
                Сторнировать
              </Button.Root>
            )}
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='small'
              onClick={onClose}
            >
              Закрыть
            </Button.Root>
          </div>
        </form>
      )}
    </ScreenState>
  );
}
